import { useUiStore, type Theme } from '@store/uiStore'

export const resolveTheme = (theme: Theme, prefersDark: boolean): 'light' | 'dark' =>
  theme === 'system' ? (prefersDark ? 'dark' : 'light') : theme

const darkQuery = () => window.matchMedia('(prefers-color-scheme: dark)')

// ponytail: the public site and login page have no dark styling yet, so dark only applies inside
// portals styled for it. index.html uses the same path rule before first paint, so a refresh does not
// flip the theme. A layout that opts in via useThemeScope also counts (a counter, so overlapping
// opt-ins such as a layout and an error page inside it don't cancel each other).
const DARK_PATHS = /^\/(tenant|super-admin|login|forgot-password|reset-password|)(\/|$)/
let scopes = 0
export function setThemeScope(value: boolean) {
  scopes = Math.max(0, scopes + (value ? 1 : -1))
  applyTheme(useUiStore.getState().theme)
}

// Where the person last pressed, so a theme change can grow out of the toggle they used.
let point = { x: 0, y: 0 }
if (typeof window !== 'undefined') {
  point = { x: window.innerWidth / 2, y: 0 }
  window.addEventListener('pointerdown', (e) => { point = { x: e.clientX, y: e.clientY } }, true)
}

type Transitioner = Document & { startViewTransition?: (update: () => void) => unknown }

function paint(dark: boolean) {
  const root = document.documentElement
  root.classList.toggle('dark', dark)
  root.style.colorScheme = dark ? 'dark' : 'light'
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#1d1d1d' : '#f6f8fb')
}

/**
 * Sets the `dark` class and `color-scheme` on <html>. With `animate`, the new theme spreads out from where the
 * person pressed (View Transitions), or cross-fades in browsers without them. Reduced motion switches at once.
 */
export function applyTheme(theme: Theme, animate = false) {
  const dark = (scopes > 0 || DARK_PATHS.test(window.location.pathname)) && resolveTheme(theme, darkQuery().matches) === 'dark'
  const root = document.documentElement
  if (root.classList.contains('dark') === dark) { paint(dark); return }
  if (!animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) { paint(dark); return }

  const doc = document as Transitioner
  if (doc.startViewTransition) {
    const r = Math.hypot(Math.max(point.x, window.innerWidth - point.x), Math.max(point.y, window.innerHeight - point.y))
    root.style.setProperty('--vt-x', `${point.x}px`)
    root.style.setProperty('--vt-y', `${point.y}px`)
    root.style.setProperty('--vt-r', `${Math.ceil(r)}px`)
    doc.startViewTransition(() => paint(dark))
  } else {
    root.classList.add('theme-fade')
    paint(dark)
    window.setTimeout(() => root.classList.remove('theme-fade'), 600)
  }
}

/** Keeps <html> in sync with the stored theme and, for `system`, with the OS setting. Call once at startup. */
export function initTheme() {
  let current = useUiStore.getState().theme
  applyTheme(current)
  useUiStore.subscribe((s) => {
    const changed = s.theme !== current
    current = s.theme
    applyTheme(s.theme, changed)
  })
  darkQuery().addEventListener('change', () => applyTheme(useUiStore.getState().theme, true))
}
